import React from 'react';
import { motion } from 'motion/react';
import { Helmet } from 'react-helmet-async';
import { TOOLS } from '../constants';
import { Link } from 'react-router-dom';
import { User as UserIcon, ShieldCheck, Star, History, ArrowUpRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { authService } from '../lib/firebase';

export default function UserPanel({ currentUser, favorites, toggleFavorite, history }: any) {
  if (!currentUser) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-10 text-center">
        <div className="w-20 h-20 bg-slate-100 dark:bg-slate-800 rounded-3xl flex items-center justify-center mb-6">
          <ShieldCheck className="w-10 h-10 text-slate-300" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
        <p className="text-slate-500 max-w-sm mb-8 text-sm font-medium">Please sign in with your Google account to access your personal dashboard and favorites.</p>
        <button 
          onClick={() => window.location.href = '/'}
          className="bg-accent text-white px-8 py-3 rounded-xl font-bold hover:shadow-lg transition-all"
        >
          Return to Home
        </button>
      </div>
    );
  }

  const favoriteTools = TOOLS.filter(t => favorites.includes(t.id));
  const recentTools = TOOLS.filter(t => history.includes(t.id));

  return (
    <div className="p-6 md:p-10 max-w-[1400px] mx-auto w-full">
      <Helmet>
        <title>My Dashboard - QuicklyUtils</title>
      </Helmet>

      <header className="mb-12 flex flex-col md:flex-row items-center gap-6 p-8 bg-white dark:bg-slate-900 rounded-[2.5rem] border border-line dark:border-slate-800 shadow-sm">
        <img 
          src={currentUser.photoURL || ''} 
          alt={currentUser.displayName || ''} 
          className="w-24 h-24 rounded-3xl border-4 border-slate-50 dark:border-slate-800 shadow-lg object-cover"
          referrerPolicy="no-referrer"
        />
        <div className="flex-1 text-center md:text-left">
          <h1 className="text-3xl font-black mb-1 dark:text-white uppercase-none">{currentUser.displayName}</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">{currentUser.email}</p>
          <div className="flex flex-wrap justify-center md:justify-start gap-3">
             <span className="px-4 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 text-[10px] font-black uppercase tracking-widest rounded-full border border-emerald-100 dark:border-emerald-800/50">Verified Account</span>
             <span className="px-4 py-1.5 bg-accent/5 text-accent text-[10px] font-black uppercase tracking-widest rounded-full border border-accent/10">Active Member</span>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Favorites List */}
        <section>
          <div className="flex items-center gap-3 mb-6 px-4">
            <Star className="w-5 h-5 text-accent fill-accent" />
            <h2 className="text-sm font-black uppercase tracking-[0.2em] text-slate-400">My Favorites</h2>
          </div>
          <div className="space-y-3">
            {favoriteTools.length > 0 ? favoriteTools.map(tool => (
              <Link 
                key={`fav-page-${tool.id}`}
                to={tool.path}
                className="flex items-center gap-4 p-5 bg-white dark:bg-slate-900 border border-line dark:border-slate-800 rounded-3xl hover:border-accent group transition-all"
              >
                <div className="w-12 h-12 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center text-slate-400 group-hover:text-accent transition-colors">
                  <tool.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-sm dark:text-white">{tool.name}</h3>
                  <p className="text-[10px] text-slate-500 line-clamp-1">{tool.description}</p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-all" />
              </Link>
            )) : (
              <div className="p-10 border-2 border-dashed border-line dark:border-slate-800 rounded-3xl text-center">
                <p className="text-xs text-slate-400 italic">No favorite tools added yet.</p>
              </div>
            )}
          </div>
        </section>

        {/* History List */}
        <section>
          <div className="flex items-center gap-3 mb-6 px-4">
            <History className="w-5 h-5 text-slate-400" />
            <h2 className="text-sm font-black uppercase tracking-[0.2em] text-slate-400">Recent Activity</h2>
          </div>
          <div className="space-y-3">
            {recentTools.length > 0 ? recentTools.map(tool => (
              <Link 
                key={`hist-page-${tool.id}`}
                to={tool.path}
                className="flex items-center gap-4 p-5 bg-white dark:bg-slate-900 border border-line dark:border-slate-800 rounded-3xl hover:border-accent group transition-all"
              >
                <div className="w-12 h-12 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center text-slate-400 group-hover:text-accent transition-colors">
                  <tool.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-sm dark:text-white">{tool.name}</h3>
                  <p className="text-[10px] text-slate-500 line-clamp-1 truncate">{tool.description}</p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-all" />
              </Link>
            )) : (
              <div className="p-10 border-2 border-dashed border-line dark:border-slate-800 rounded-3xl text-center">
                <p className="text-xs text-slate-400 italic">History will appear here as you use tools.</p>
              </div>
            )}
          </div>
        </section>
      </div>

      <div className="mt-12 p-8 bg-slate-900 rounded-[2.5rem] text-white flex flex-col md:flex-row items-center justify-between gap-6 overflow-hidden relative group">
        <div className="relative z-10">
          <h3 className="text-xl font-bold mb-1">Account Management</h3>
          <p className="text-slate-400 text-xs">Your data is synchronized across all your devices via Google Cloud.</p>
        </div>
        <button 
          onClick={() => authService.signOut()}
          className="relative z-10 px-8 py-3 bg-white text-slate-900 rounded-xl font-bold hover:bg-slate-100 transition-all text-sm"
        >
          Sign Out
        </button>
        <UserIcon className="absolute -right-8 -bottom-8 w-40 h-40 text-white/5 rotate-12 group-hover:scale-110 transition-transform" />
      </div>
    </div>
  );
}
